/**
 * Badge component for labels, status indicators, and tags.
 *
 * Uses CVA for type-safe variant handling.
 * Common uses: status badges, notification counts, category tags.
 */

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Badge variant definitions using CVA.
 */
const badgeVariants = cva(
  // Base styles - common to all badges
  'inline-flex items-center justify-center font-medium transition-colors',
  {
    variants: {
      variant: {
        // Default: Gray/neutral
        default:
          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
        // Primary: Brand color
        primary:
          'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
        // Secondary: Outlined
        secondary:
          'border border-gray-300 bg-transparent text-gray-700 dark:border-gray-700 dark:text-gray-300',
        // Success: Green
        success:
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        // Warning: Yellow/amber
        warning:
          'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        // Error/Danger: Red
        error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        // Info: Blue
        info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        // Solid variants for high contrast
        solidPrimary: 'bg-primary-600 text-white dark:bg-primary-600',
        solidSuccess: 'bg-green-600 text-white dark:bg-green-600',
        solidWarning: 'bg-amber-600 text-white dark:bg-amber-600',
        solidError: 'bg-red-600 text-white dark:bg-red-600',
        solidInfo: 'bg-blue-600 text-white dark:bg-blue-600',
      },
      size: {
        xs: 'px-1.5 py-0.5 text-xs',
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1.5 text-sm',
        xl: 'px-4 py-2 text-base',
      },
      shape: {
        rounded: 'rounded-md',
        pill: 'rounded-full',
        square: 'rounded-none',
      },
      dot: {
        true: 'gap-1.5',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
      shape: 'rounded',
      dot: false,
    },
  }
)

/**
 * Badge component props.
 */
export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Shows colored dot before text */
  showDot?: boolean
  /** Custom dot color (overrides variant dot color) */
  dotColor?: string
  /** Icon to display before text */
  icon?: React.ReactNode
}

/**
 * Badge component for labels and status indicators.
 *
 * Features:
 * - Multiple variants (default, primary, success, warning, error, info)
 * - Solid variants for high contrast
 * - Size options (xs to xl)
 * - Shape options (rounded, pill/circular, square)
 * - Optional status dot indicator
 * - Optional icon support
 *
 * @example
 * ```tsx
 * // Status badge
 * <Badge variant="success" shape="pill">
 *   Active
 * </Badge>
 *
 * // With dot indicator
 * <Badge variant="warning" showDot>
 *   Pending Review
 * </Badge>
 *
 * // Notification count
 * <Badge variant="solidError" shape="pill" size="xs">
 *   3
 * </Badge>
 *
 * // Category tag
 * <Badge variant="secondary">
 *   React
 * </Badge>
 * ```
 */
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      showDot = false,
      dotColor,
      icon,
      children,
      ...props
    },
    ref
  ) => {
    // Determine dot color based on variant
    const getDotColor = () => {
      if (dotColor) return dotColor

      switch (variant) {
        case 'primary':
        case 'solidPrimary':
          return 'bg-primary-600'
        case 'success':
        case 'solidSuccess':
          return 'bg-green-600'
        case 'warning':
        case 'solidWarning':
          return 'bg-amber-600'
        case 'error':
        case 'solidError':
          return 'bg-red-600'
        case 'info':
        case 'solidInfo':
          return 'bg-blue-600'
        default:
          return 'bg-gray-600'
      }
    }

    return (
      <span
        ref={ref}
        className={cn(
          badgeVariants({
            variant,
            size,
            shape,
            dot: showDot,
            className,
          })
        )}
        {...props}
      >
        {showDot && (
          <span
            className={cn('h-1.5 w-1.5 rounded-full', getDotColor())}
            aria-hidden="true"
          />
        )}
        {icon && <span className="flex items-center">{icon}</span>}
        {children}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

// Default export for convenience
export default Badge

// Export variants for reuse
export { badgeVariants }
